import { useEffect, useRef } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  CLASSIFICATION_OPTIONS,
  MINING_METHOD_OPTIONS,
} from "./resourceUtils";
import { COMPANY_LIFECYCLE_OPTIONS } from "../../constants/companyFields";

export default function ResourceDialogs({
  stageOptions,
  commodities,
  addResourceReserveOpen,
  addResourceReportOpen,
  resourceReportDialogMode,
  resourceReportDeleteTarget,
  economicScenarioSaveState,
  economicScenarioDeleteTarget,
  resourceReserveForm,
  resourceReportForm,
  resourceReserveSaveState,
  resourceReportSaveState,
  resourceReserveDialogMode,
  resourceReserveDeleteTarget,
  classificationTable,
  resourceReportScenarioReport,
  resourceReportScenarioReserveId,
  resourceReportScenarioDrafts = [],
  resourceReportDuplicateNotice,
  resourceReserveNameById,
  resourceReportSourceTitleOptions = [],
  resourceReportSourceUrlOptions = [],
  onDraftScenarioFieldChange = () => {},
  onDraftScenarioPriceChange = () => {},
  onAddDraftScenarioPriceRow = () => {},
  onRemoveDraftScenarioPriceRow = () => {},
  onRemoveDraftScenario = () => {},
  onCloseResourceReserve,
  onCancelDeleteResourceReserve,
  onConfirmDeleteResourceReserve,
  onCloseResourceReport,
  onResourceReserveFieldChange,
  onResourceReportFieldChange,
  onCommoditySelectionChange,
  onClassificationRowChange,
  onSubmitResourceReserve,
  onUpdateResourceReserve,
  onSubmitResourceReport,
  onUpdateResourceReport,
  onCancelDeleteResourceReport,
  onConfirmDeleteResourceReport,
  onAddScenarioFromReport,
  onCancelDeleteEconomicScenario,
  onConfirmDeleteEconomicScenario,
}) {
  const defaultDialogContentSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mt: 1,
  };
  const excelTextFieldSx = {
    "& .MuiInputBase-input": {
      padding: "6px 8px",
      fontSize: "0.875rem", 
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      transform: "translate(8px, 8px) scale(1)",
    },

    "& .MuiInputLabel-shrink": {
      transform: "translate(8px, -6px) scale(0.85)",
    },
  };
  const resolveReserveName = (reserveId) => {
    if (!reserveId || !resourceReserveNameById) return "";
    return (
      resourceReserveNameById[reserveId] ||
      resourceReserveNameById[`${reserveId}`] ||
      ""
    );
  };
  const squareTextFieldSx = {
    ...excelTextFieldSx,
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
    },
  };
  const mlbsToMt = 0.00045359237;
  const shouldShowMlbTooltip = (commodity) => {
    const name = (commodity?.name || "").toLowerCase();
    return ["zinc", "copper", "nickel", "molybdenum", "sulphur"].some((item) =>
      name.includes(item)
    );
  };
  const formatMlbToMt = (mlb) => {
    const value = mlb * mlbsToMt;
    return value.toFixed(4).replace(/\.?0+$/, "");
  };
  const draftScenarioEndRef = useRef(null);
  const selectedCommodityIds = classificationTable?.commodity_ids || [];
  const selectedCommodities = selectedCommodityIds
    .map((commodityId) =>
      commodities.find((item) => `${item.id}` === `${commodityId}`)
    )
    .filter(Boolean);
  const tableMinWidth = Math.max(640, 320 + selectedCommodities.length * 220);
  const reportReserveName = resolveReserveName(resourceReportForm.resource_reserve_id);
  const updateResourceReportField = (field, value) => {
    onResourceReportFieldChange(field)({ target: { value } });
  };

  useEffect(() => {
    if (resourceReportScenarioDrafts.length === 0) return;
    draftScenarioEndRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [resourceReportScenarioDrafts.length]);

  return (
    <>
      <Dialog open={addResourceReserveOpen} onClose={onCloseResourceReserve} fullWidth maxWidth="sm">
        <DialogTitle>
          {resourceReserveDialogMode === "edit"
            ? "Edit Resources & Reserves"
            : "Add Resources & Reserves"}
        </DialogTitle>
        <DialogContent sx={defaultDialogContentSx}>
          {resourceReserveSaveState.error && (
            <Alert severity="error">{resourceReserveSaveState.error}</Alert>
          )}
            <TextField
              label="Resources & Reserves name"
              required
              value={resourceReserveForm.name}
              onChange={onResourceReserveFieldChange("name")}
              fullWidth
              sx={excelTextFieldSx}
            />
            <TextField
              select
              label="Mining method"
              value={resourceReserveForm.mining_method}
              onChange={onResourceReserveFieldChange("mining_method")}
              fullWidth
              sx={excelTextFieldSx}
            >
            <MenuItem value="">Select method</MenuItem>
            {MINING_METHOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
            <TextField
              select
              label="Lifecycle"
              value={resourceReserveForm.lifecycle}
              onChange={onResourceReserveFieldChange("lifecycle")}
              fullWidth
              sx={excelTextFieldSx}
            >
            {COMPANY_LIFECYCLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
            <TextField
              select
              label="Lifecycle stage"
              value={resourceReserveForm.lifecycle_stage}
              onChange={onResourceReserveFieldChange("lifecycle_stage")}
              fullWidth
              sx={excelTextFieldSx}
            >
            <MenuItem value="">Select stage</MenuItem>
            {stageOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Dates
            </Typography>
            {resourceReserveForm.lifecycle === "na" && (
              <Typography variant="caption" color="text.secondary">
                Select a lifecycle to display date fields.
              </Typography>
            )}
            {["exploration", "development"].includes(resourceReserveForm.lifecycle) && (
              <TextField
                label="Expected first production year"
                value={resourceReserveForm.expected_first_production_year}
                onChange={onResourceReserveFieldChange("expected_first_production_year")}
                type="number"
                fullWidth
                sx={excelTextFieldSx}
              />
            )}
            {resourceReserveForm.lifecycle === "production" && (
              <TextField
                label="Start year of operation"
                value={resourceReserveForm.start_year_of_operation}
                onChange={onResourceReserveFieldChange("start_year_of_operation")}
                type="number"
                fullWidth
                sx={excelTextFieldSx}
              />
            )}
            {resourceReserveForm.lifecycle === "closure" && (
              <TextField
                label="Closure/reclamation year"
                value={resourceReserveForm.closure_or_reclamation_year}
                onChange={onResourceReserveFieldChange("closure_or_reclamation_year")}
                type="number"
                fullWidth
                sx={excelTextFieldSx}
              />
            )}
            <TextField
              label="Notes"
              value={resourceReserveForm.notes}
              onChange={onResourceReserveFieldChange("notes")}
              fullWidth
              multiline
              minRows={2}
              sx={excelTextFieldSx}
            />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseResourceReserve}>Cancel</Button>
          <Button
            variant="contained"
            onClick={
              resourceReserveDialogMode === "edit"
                ? onUpdateResourceReserve
                : onSubmitResourceReserve
            }
            disabled={resourceReserveSaveState.loading}
          >
            {resourceReserveSaveState.loading
              ? "Saving..."
              : resourceReserveDialogMode === "edit"
              ? "Update Resources & Reserves"
              : "Save Resources & Reserves"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addResourceReportOpen} onClose={onCloseResourceReport} fullWidth maxWidth="xl">
        <DialogTitle>
          {resourceReportDialogMode === "edit"
            ? `Edit Resource Estimate${reportReserveName ? ` for ${reportReserveName}` : ""}`
            : `Add Resource Estimate${reportReserveName ? ` for ${reportReserveName}` : ""}`}
        </DialogTitle>
        <DialogContent sx={defaultDialogContentSx}>
          {resourceReportDuplicateNotice && (
            <Alert severity="info">{resourceReportDuplicateNotice}</Alert>
          )}
          {resourceReportSaveState.error && (
            <Alert severity="error">{resourceReportSaveState.error}</Alert>
          )}
          <Typography variant="subtitle2" fontWeight={700}>
            Report Meta
          </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                label="As-of date"
                type="date"
                value={resourceReportForm.as_of_date}
                onChange={onResourceReportFieldChange("as_of_date")}
                InputLabelProps={{ shrink: true }}
                required={!resourceReportForm.estimate_period_unknown}
                fullWidth
                sx={excelTextFieldSx}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(resourceReportForm.estimate_period_unknown)}
                    onChange={onResourceReportFieldChange("estimate_period_unknown")}
                  />
                }
                label="Historical estimate"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Autocomplete
              freeSolo
              options={resourceReportSourceTitleOptions}
              value={resourceReportForm.source_title || ""}
              inputValue={resourceReportForm.source_title || ""}
              onInputChange={(_event, value) => updateResourceReportField("source_title", value)}
              onChange={(_event, value) => updateResourceReportField("source_title", value || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Source title"
                  fullWidth
                  sx={excelTextFieldSx}
                />
              )}
            />
            <Autocomplete
              freeSolo
              options={resourceReportSourceUrlOptions}
              value={resourceReportForm.source_url || ""}
              inputValue={resourceReportForm.source_url || ""}
              onInputChange={(_event, value) => updateResourceReportField("source_url", value)}
              onChange={(_event, value) => updateResourceReportField("source_url", value || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Source URL"
                  fullWidth
                  sx={excelTextFieldSx}
                />
              )}
            />
          </Box>
            <TextField
              label="Source notes"
              value={resourceReportForm.source_notes}
              onChange={onResourceReportFieldChange("source_notes")}
              fullWidth
              multiline
              minRows={2}
              sx={excelTextFieldSx}
            />
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            Commodity Estimates
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Enter resource estimates by classification. Units are enforced automatically.
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              label="Commodities"
              value={selectedCommodityIds}
              onChange={onCommoditySelectionChange}
              required
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected || []).map((selectedId) => {
                      const commodity = commodities.find(
                        (item) => `${item.id}` === `${selectedId}`
                      );
                      return (
                        <Chip
                          key={selectedId}
                          label={commodity?.name || "Commodity"}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                ),
              }}
              sx={excelTextFieldSx}
            >
              {commodities.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 1.5,
                overflowX: "auto",
              }}
            >
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: `${tableMinWidth}px`,
                }}
              >
                <Box component="thead">
                  <Box component="tr" sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
                    <Box component="th" rowSpan={2} sx={{ px: 2, py: 1, textAlign: "left" }}>
                      <Typography variant="caption" fontWeight={700}>
                        Classification
                      </Typography>
                    </Box>
                    <Box component="th" rowSpan={2} sx={{ px: 2, py: 1, textAlign: "center" }}>
                      <Typography variant="caption" fontWeight={700}>
                        Tonnage (Mt)
                      </Typography>
                    </Box>
                    {selectedCommodities.length > 0 ? (
                      <>
                        <Box
                          component="th"
                          colSpan={selectedCommodities.length}
                          sx={{ px: 2, py: 1, textAlign: "center" }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            Grade
                          </Typography>
                        </Box>
                        <Box
                          component="th"
                          colSpan={selectedCommodities.length}
                          sx={{ px: 2, py: 1, textAlign: "center" }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            Contained Metal
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Box
                        component="th"
                        colSpan={2}
                        sx={{ px: 2, py: 1, textAlign: "left" }}
                      >
                        <Typography variant="caption" fontWeight={700}>
                          Grade / Contained
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box component="tr" sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
                    {selectedCommodities.length > 0 ? (
                      <>
                        {selectedCommodities.map((commodity, commodityIndex) => (
                          <Box
                            component="th"
                            key={`grade-${commodity.id}`}
                            sx={{
                              px: 0,
                              pb: 1,
                              textAlign: "center",
                              pr:
                                commodityIndex === selectedCommodities.length - 1
                                  ? 2
                                  : 0,
                            }}
                          >
                            <Typography variant="caption" fontWeight={700}>
                              {commodity.name} ({commodity.grade_unit || "unit"})
                            </Typography>
                          </Box>
                        ))}
                        {selectedCommodities.map((commodity, commodityIndex) => (
                          <Box
                            component="th"
                            key={`contained-${commodity.id}`}
                            sx={{
                              px: 0,
                              pb: 1,
                              textAlign: "center",
                              pl: commodityIndex === 0 ? 2 : 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="caption" fontWeight={700}>
                                {commodity.name} ({commodity.contained_unit || "unit"})
                              </Typography>
                              {shouldShowMlbTooltip(commodity) &&
                                String(commodity?.contained_unit || "")
                                  .toLowerCase()
                                  .includes("mt") && (
                                  <Tooltip
                                    title={`Convert Mlb to Mt: multiply by ${formatMlbToMt(
                                      1
                                    )}. (Mt = Mlb x ${formatMlbToMt(1)}; 1 Mt = 2204.6 Mlb)`}
                                    arrow
                                  >
                                    <Box
                                      component="span"
                                      sx={{ display: "inline-flex", alignItems: "center" }}
                                    >
                                      <InfoOutlinedIcon
                                        sx={{ fontSize: 14, color: "text.secondary" }}
                                      />
                                    </Box>
                                  </Tooltip>
                                )}
                            </Box>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <Box
                        component="th"
                        colSpan={2}
                        sx={{ px: 2, pb: 1, textAlign: "left" }}
                      >
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Select commodities to add columns
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box component="tbody">
                  {(classificationTable?.rows || []).map((row, rowIndex) => (
                    <Box
                      component="tr"
                      key={`${row.classification_level}-${rowIndex}`}
                      sx={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}
                    >
                      <Box component="td" sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2">
                          {CLASSIFICATION_OPTIONS.find(
                            (opt) => opt.value === row.classification_level
                          )?.label || row.classification_level}
                        </Typography>
                      </Box>
                      <Box component="td" sx={{ px: 2, py: 1 }}>
                        <TextField
                          value={row.tonnage_mt}
                          onChange={onClassificationRowChange(rowIndex, "tonnage_mt")}
                          type="number"
                          required
                          size="small"
                          fullWidth
                          sx={squareTextFieldSx}
                        />
                      </Box>
                      {selectedCommodities.length > 0 ? (
                        <>
                          {selectedCommodities.map((commodity, commodityIndex) => (
                            <Box
                              component="td"
                              key={`grade-${commodity.id}-${rowIndex}`}
                              sx={{
                                px: 0,
                                py: 1,
                                pr:
                                  commodityIndex === selectedCommodities.length - 1
                                    ? 2
                                    : 0,
                              }}
                            >
                              <TextField
                                value={row.grade_values?.[`${commodity.id}`] ?? ""}
                                onChange={onClassificationRowChange(
                                  rowIndex,
                                  "grade_value",
                                  commodity.id
                                )}
                                type="number"
                                required
                                size="small"
                                fullWidth
                                sx={squareTextFieldSx}
                              />
                            </Box>
                          ))}
                          {selectedCommodities.map((commodity, commodityIndex) => (
                            <Box
                              component="td"
                              key={`contained-${commodity.id}-${rowIndex}`}
                              sx={{
                                px: 0,
                                py: 1,
                                pl: commodityIndex === 0 ? 2 : 0,
                              }}
                            >
                              <TextField
                                value={row.contained_values?.[`${commodity.id}`] ?? ""}
                                onChange={onClassificationRowChange(
                                  rowIndex,
                                  "contained_value",
                                  commodity.id
                                )}
                                type="number"
                                required
                                size="small"
                                fullWidth
                                sx={squareTextFieldSx}
                              />
                            </Box>
                          ))}
                        </>
                      ) : (
                        <>
                          <Box component="td" sx={{ px: 2, py: 1 }} />
                          <Box component="td" sx={{ px: 2, py: 1 }} />
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Stack>
          <Divider />

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700}>
              Economic Scenarios (NPV)
            </Typography>
          </Stack>
          {resourceReportScenarioReport?.economic_scenarios?.length ? (
            <Box
              sx={{
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 1.5,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 0.8fr 0.8fr 0.8fr 1.4fr 1fr 0.8fr 0.9fr",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  backgroundColor: "rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="caption" fontWeight={700}>
                  Name
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Currency
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Tax
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Discount
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Price Assumptions
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  NPV
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  IRR
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Payback Period
                </Typography>
              </Box>
              <Stack spacing={0} sx={{ px: 1, py: 0.75 }}>
                {(resourceReportScenarioReport?.economic_scenarios || []).map((scenario, index) => {
                  const priceDeck = (scenario.prices || [])
                    .map((price) => {
                      const commodityName =
                        price.commodity_detail?.name ||
                        price.commodity?.name ||
                        commodities.find((item) => item.id === price.commodity_id)?.name ||
                        "Commodity";
                      const priceValue =
                        price.price !== null && price.price !== undefined ? price.price : "";
                      const currency = scenario.currency || "";
                      return `${commodityName} ${priceValue}${currency ? ` ${currency}` : ""}`.trim();
                    })
                    .join(", ");
                  return (
                    <Box
                      key={scenario.id || scenario.temp_id || `scenario-${index}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "1.2fr 0.8fr 0.8fr 0.8fr 1.4fr 1fr 0.8fr 0.9fr",
                        gap: 1,
                        alignItems: "center",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        {scenario.label || `Scenario ${index + 1}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scenario.currency || "USD"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scenario.tax_treatment || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scenario.discount_rate !== null &&
                        scenario.discount_rate !== undefined
                          ? `${scenario.discount_rate}%`
                          : "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {priceDeck || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scenario.npv_value !== null && scenario.npv_value !== undefined
                          ? scenario.npv_value
                          : "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scenario.irr_value !== null &&
                        scenario.irr_value !== undefined
                          ? `${scenario.irr_value}%`
                          : "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scenario.payback_period !== null &&
                        scenario.payback_period !== undefined
                          ? `${scenario.payback_period} yrs`
                          : "N/A"}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ) : null}
          {economicScenarioSaveState?.error && (
            <Alert severity="error">{economicScenarioSaveState.error}</Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            {resourceReportScenarioDrafts.map((scenario, scenarioIndex) => (
              <Box
                key={scenario.temp_id || `draft-scenario-${scenarioIndex}`}
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1.5,
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Economic Scenario {scenarioIndex + 1}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => onRemoveDraftScenario(scenario.temp_id)}
                    sx={{ minWidth: "auto", px: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </Button>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mt: 2,
                  }}
                >
                    <TextField
                      label="Scenario Name"
                      value={scenario.label || ""}
                      onChange={(event) =>
                        onDraftScenarioFieldChange(scenario.temp_id, "label", event.target.value)
                      }
                      fullWidth
                      required
                      sx={excelTextFieldSx}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Reporting currency:
                      </Typography>
                      <Chip
                        size="small"
                        label={scenario.currency || "USD"}
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <TextField
                      select
                      label="Tax treatment"
                      value={scenario.tax_treatment || ""}
                      onChange={(event) =>
                      onDraftScenarioFieldChange(
                        scenario.temp_id,
                        "tax_treatment",
                        event.target.value
                      )
                    }
                    fullWidth
                    required
                    sx={excelTextFieldSx}
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    <MenuItem value="pre_tax">Pre-tax</MenuItem>
                    <MenuItem value="after_tax">After-tax</MenuItem>
                  </TextField>
                    <TextField
                      label="Discount rate (%)"
                      value={scenario.discount_rate ?? ""}
                      onChange={(event) =>
                        onDraftScenarioFieldChange(
                          scenario.temp_id,
                        "discount_rate",
                        event.target.value
                      )
                      }
                      type="number"
                      fullWidth
                      required
                      sx={excelTextFieldSx}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <TextField
                      label="NPV value"
                      value={scenario.npv_value ?? ""}
                      onChange={(event) =>
                        onDraftScenarioFieldChange(
                        scenario.temp_id,
                        "npv_value",
                        event.target.value
                      )
                      }
                      type="number"
                      fullWidth
                      required
                      sx={excelTextFieldSx}
                    />
                    <TextField
                      label="IRR (%)"
                      value={scenario.irr_value ?? ""}
                      onChange={(event) =>
                        onDraftScenarioFieldChange(
                          scenario.temp_id,
                          "irr_value",
                          event.target.value
                        )
                      }
                      type="number"
                      fullWidth
                      sx={excelTextFieldSx}
                    />
                    <TextField
                      label="Payback Period (Years)"
                      value={scenario.payback_period ?? ""}
                      onChange={(event) =>
                        onDraftScenarioFieldChange(
                          scenario.temp_id,
                          "payback_period",
                          event.target.value
                        )
                      }
                      type="number"
                      fullWidth
                      sx={excelTextFieldSx}
                    />
                  </Box>
                  <TextField
                    label="Notes"
                  value={scenario.notes || ""}
                  onChange={(event) =>
                    onDraftScenarioFieldChange(
                      scenario.temp_id,
                      "notes",
                      event.target.value
                    )
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 2, ...excelTextFieldSx }}
                  />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Commodity Price Assumptions
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {(scenario.prices || []).map((row, rowIndex) => (
                    <Box
                      key={`${scenario.temp_id}-price-${rowIndex}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1fr auto" },
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                        <TextField
                          select
                          label="Commodity"
                          value={row.commodity_id || ""}
                          onChange={(event) =>
                          onDraftScenarioPriceChange(
                            scenario.temp_id,
                            rowIndex,
                            "commodity_id",
                              event.target.value
                            )
                          }
                          fullWidth
                          sx={excelTextFieldSx}
                        >
                        <MenuItem value="">Select commodity</MenuItem>
                        {commodities.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </TextField>
                        <TextField
                          label="Price"
                          value={row.price ?? ""}
                          onChange={(event) =>
                            onDraftScenarioPriceChange(
                            scenario.temp_id,
                            rowIndex,
                            "price",
                              event.target.value
                            )
                          }
                          type="number"
                          required
                          fullWidth
                          sx={excelTextFieldSx}
                        />
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          onRemoveDraftScenarioPriceRow(scenario.temp_id, rowIndex)
                        }
                        sx={{ minWidth: "auto" }}
                      >
                        <CloseIcon fontSize="small" />
                      </Button>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => onAddDraftScenarioPriceRow(scenario.temp_id)}
                  >
                    Add price row
                  </Button>
                </Stack>
              </Box>
            ))}
            <Box ref={draftScenarioEndRef} />
          </Stack>
          <Stack alignItems="center" sx={{ mt: 1.5 }}>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={() =>
                onAddScenarioFromReport(
                  resourceReportScenarioReport,
                  resourceReportScenarioReserveId
                )
              }
            >
              Add Scenario
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseResourceReport}>Cancel</Button>
          <Button
            variant="contained"
            onClick={resourceReportDialogMode === "edit" ? onUpdateResourceReport : onSubmitResourceReport}
            disabled={resourceReportSaveState.loading}
          >
            {resourceReportSaveState.loading
              ? "Saving..."
              : resourceReportDialogMode === "edit"
              ? "Update estimate"
              : "Save estimate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(resourceReportDeleteTarget)}
        onClose={onCancelDeleteResourceReport}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete resource estimate?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete the resource estimate{" "}
            <strong>{resourceReportDeleteTarget?.asOfDate || "for this report"}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelDeleteResourceReport}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteResourceReport}>
            Confirm delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(economicScenarioDeleteTarget)}
        onClose={onCancelDeleteEconomicScenario}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete economic scenario?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete the economic scenario{" "}
            <strong>{economicScenarioDeleteTarget?.label || "for this report"}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelDeleteEconomicScenario}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteEconomicScenario}>
            Confirm delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(resourceReserveDeleteTarget)}
        onClose={onCancelDeleteResourceReserve}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete resources & reserves?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete the resources & reserves{" "}
            <strong>{resourceReserveDeleteTarget?.label || "entry"}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelDeleteResourceReserve}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDeleteResourceReserve}>
            Confirm delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
